import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';

import androidBadgePlaceholder from '../../assets/google-play-badge-placeholder.png';
import appleBadgePlaceholder from '../../assets/app-store-badge-placeholder.png';
import { CLIENT_BRAND_NAME } from '../../branding';
import { PageHeader } from '../PageHeader';

type StoreDownload = {
  platform: 'Apple' | 'Android';
  href: string;
  badge: string;
  description: string;
  accent: string;
  Icon: typeof AppleIcon;
};

// Deliberately non-official placeholders; replace them with approved hosted official badges before merge.
const STORE_DOWNLOADS: readonly StoreDownload[] = [
  {
    platform: 'Apple',
    href: 'https://apps.apple.com/us/app/fieldops-ai/id6784761238',
    badge: appleBadgePlaceholder,
    description: 'Download the mobile app for your iPhone or iPad.',
    accent: '#1D1D1F',
    Icon: AppleIcon,
  },
  {
    platform: 'Android',
    href: 'https://play.google.com/apps/testing/com.fieldops',
    badge: androidBadgePlaceholder,
    description: 'Join the Android testing program to get ready for your next shift.',
    accent: '#2E7D32',
    Icon: AndroidIcon,
  },
];

export const AgentHomePage = () => (
  <>
    <PageHeader title="Home" />
    <Stack component="section" aria-label={`${CLIENT_BRAND_NAME} mobile app downloads`} spacing={3}>
      <Typography component="p" variant="body1" color="text.secondary" sx={{ maxWidth: 640, m: 0 }}>
        Take {CLIENT_BRAND_NAME} with you and keep your canvassing work close at hand. Choose your device to download the app.
      </Typography>
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ alignItems: 'stretch' }}>
        {STORE_DOWNLOADS.map(({ platform, href, badge, description, accent, Icon }) => (
          <Grid key={platform} size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Card
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              aria-label={`Download ${CLIENT_BRAND_NAME} for ${platform} (opens in a new tab)`}
              sx={{
                display: 'flex',
                flex: 1,
                borderColor: 'divider',
                borderRadius: '16px',
                color: 'text.primary',
                textDecoration: 'none',
                transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                '&:hover': {
                  borderColor: accent,
                  boxShadow: '0 8px 24px rgba(12, 12, 12, 0.1)',
                },
                '&:active': {
                  transform: 'translateY(1px)',
                },
                '&:focus-visible': {
                  outline: `3px solid ${accent}`,
                  outlineOffset: 3,
                },
              }}
            >
              <CardContent sx={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'flex-start', p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Icon aria-hidden="true" sx={{ color: accent }} />
                  <Typography component="p" variant="overline" sx={{ color: accent, fontWeight: 700, lineHeight: 1.2, m: 0 }}>
                    {platform}
                  </Typography>
                </Box>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, m: 0 }}>
                  {platform === 'Apple' ? 'Download for Apple devices' : 'Join Android testing'}
                </Typography>
                <Typography component="p" variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  {description}
                </Typography>
                <Box component="img" src={badge} alt="" sx={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: 48, mt: 'auto' }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  </>
);
